// UnifiedCalendar — container around CalendarGrid. Fetches the cross-module
// aggregation, renders the type legend (clickable filters), a secondary-dates
// toggle, a day side-panel with deep links, and native ad-hoc event CRUD.
// Reused by: project Calendar tab, global /app/calendar, and the portal calendar.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Plus, ChevronDown, X, Save, Loader2, ExternalLink, CalendarOff, Users, Truck,
    CalendarCheck, UsersRound, Bell, CheckSquare,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useView } from "@/context/ViewContext";
import Pill from "@/components/Pill";
import CalendarGrid, { isoOf, todayIso } from "./CalendarGrid";
import CalendarItemQuickView from "./CalendarItemQuickView";
import DaySummaryQuickView from "./DaySummaryQuickView";
import { VisitDialog } from "@/components/visits/VisitFormDialog";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const QUICK_CREATE_PRIORITIES = ["Low", "Medium", "High", "Critical"];

const CAN_EDIT_ROLES = new Set(["director", "administrator", "consultant"]);
const STATUS_TONE = { Scheduled: "blue", Completed: "green", Done: "green", Cancelled: "neutral",
    Open: "amber", "In progress": "blue", Closed: "neutral", Issued: "blue", Approved: "green" };

function monthBounds(year, month) {
    const last = new Date(year, month, 0).getDate();
    return { from: isoOf(year, month, 1), to: isoOf(year, month, last) };
}

export default function UnifiedCalendar({ clientId = null, projectId = null, buildingId = null,
    canEdit = false, role = "", showScopeNote = false }) {
    const navigate = useNavigate();
    const today = new Date();
    const [year, setYear] = useState(today.getFullYear());
    const [month, setMonth] = useState(today.getMonth() + 1);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [meta, setMeta] = useState({ colours: {}, module_labels: {}, native_types: [], event_statuses: [] });
    const [hidden, setHidden] = useState(new Set());          // module keys toggled OFF
    const [includeSecondary, setIncludeSecondary] = useState(false);
    const [selectedDate, setSelectedDate] = useState(todayIso());
    const [dialog, setDialog] = useState(null);               // null | {mode, initial}
    const [quickItem, setQuickItem] = useState(null);         // selected calendar item for right-hand quick view
    const [visitDialog, setVisitDialog] = useState({ open: false, date: null });
    const [actionDialog, setActionDialog] = useState({ open: false, date: null });
    const [visitMeta, setVisitMeta] = useState({ visit_types: [], statuses: [], recurrence_options: [] });
    const [buildings, setBuildings] = useState([]);
    const [templates, setTemplates] = useState([]);
    const { clients } = useView();

    useEffect(() => {
        api.get("/calendar/meta").then((r) => setMeta(r.data)).catch(() => {});
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { from, to } = monthBounds(year, month);
            const params = { from, to, include_secondary_dates: includeSecondary };
            if (clientId) params.client_id = clientId;
            if (projectId) params.project_id = projectId;
            if (buildingId) params.building_id = buildingId;
            const { data } = await api.get("/calendar/aggregate", { params });
            setEvents(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [year, month, clientId, projectId, buildingId, includeSecondary]);

    useEffect(() => { load(); }, [load]);

    const visible = useMemo(() => events.filter((e) => !hidden.has(e.source_module)), [events, hidden]);
    const eventsByDay = useMemo(() => {
        const m = {};
        visible.forEach((e) => { (m[e.date] || (m[e.date] = [])).push(e); });
        return m;
    }, [visible]);
    const dayEvents = selectedDate ? (eventsByDay[selectedDate] || []) : [];

    // legend = modules present in meta (stable order)
    const legend = useMemo(() => Object.entries(meta.module_labels || {}), [meta]);

    function toggleModule(mod) {
        setHidden((prev) => {
            const next = new Set(prev);
            next.has(mod) ? next.delete(mod) : next.add(mod);
            return next;
        });
    }

    function changeMonth(dir) {
        let m = month + dir, y = year;
        if (m < 1) { m = 12; y -= 1; }
        if (m > 12) { m = 1; y += 1; }
        setMonth(m); setYear(y);
    }
    function goToday() {
        setYear(today.getFullYear()); setMonth(today.getMonth() + 1); setSelectedDate(todayIso());
    }

    async function deleteEvent(e) {
        if (!window.confirm(`Delete "${e.title}"?`)) return;
        try {
            await api.delete(`/calendar/events/${e.source_id}`);
            toast.success("Event deleted");
            setQuickItem(null);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    }

    const canAdd = canEdit && CAN_EDIT_ROLES.has(role) && clientId;

    useEffect(() => {
        if (!canAdd) return;
        api.get("/visits/meta").then((r) => setVisitMeta(r.data)).catch(() => {});
        api.get("/buildings", { params: { client_id: clientId } }).then((r) => setBuildings(r.data)).catch(() => {});
        api.get("/forms/templates", { params: { status: "published" } }).then((r) => setTemplates(r.data)).catch(() => {});
    }, [canAdd, clientId]);

    function openQuickCreate(kind, dateStr) {
        const date = dateStr || selectedDate || todayIso();
        if (kind === "visit") setVisitDialog({ open: true, date });
        else if (kind === "action") setActionDialog({ open: true, date });
        else setDialog({ mode: "create", initial: { start: date, event_type: kind === "meeting" ? "Meeting" : "Reminder" } });
    }

    return (
        <div className="space-y-4" data-testid="unified-calendar">
            {/* Toolbar: legend filters + secondary toggle + add */}
            <div className="rounded-2xl bg-white border border-[#E5E0DA] p-4 flex flex-wrap items-center gap-2">
                <span className="text-[11px] font-bold uppercase tracking-wider text-[#9A968F] mr-1">Show</span>
                {legend.map(([mod, label]) => {
                    const off = hidden.has(mod);
                    const colour = meta.colours?.[label] || "#6F6A64";
                    return (
                        <button key={mod} data-testid={`calendar-filter-${mod}`} onClick={() => toggleModule(mod)}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11.5px] font-semibold transition ${off ? "opacity-40 border-[#E5E0DA] bg-white" : "border-transparent text-white"}`}
                            style={off ? {} : { background: colour }}>
                            <span className="w-2 h-2 rounded-full" style={{ background: off ? colour : "rgba(255,255,255,.85)" }} />
                            {label}
                        </button>
                    );
                })}
                <div className="flex-1" />
                <label className="inline-flex items-center gap-2 text-[12px] text-[#6F6A64] cursor-pointer select-none" data-testid="calendar-secondary-toggle">
                    <input type="checkbox" checked={includeSecondary} onChange={(e) => setIncludeSecondary(e.target.checked)} />
                    Show secondary dates (completed / submitted / review)
                </label>
                {canAdd && (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button data-testid="calendar-add-event"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-[13px] font-semibold"
                                style={{ background: "var(--brand-accent, #C75A1B)" }}><Plus size={14} /> Add <ChevronDown size={13} /></button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-[#9A968F]">Create new</DropdownMenuLabel>
                            <DropdownMenuItem data-testid="calendar-add-visit" onClick={() => openQuickCreate("visit")}>
                                <CalendarCheck className="mr-2 h-3.5 w-3.5" /> Site visit
                            </DropdownMenuItem>
                            <DropdownMenuItem data-testid="calendar-add-meeting" onClick={() => openQuickCreate("meeting")}>
                                <UsersRound className="mr-2 h-3.5 w-3.5" /> Meeting
                            </DropdownMenuItem>
                            <DropdownMenuItem data-testid="calendar-add-reminder" onClick={() => openQuickCreate("reminder")}>
                                <Bell className="mr-2 h-3.5 w-3.5" /> Reminder
                            </DropdownMenuItem>
                            <DropdownMenuItem data-testid="calendar-add-action" onClick={() => openQuickCreate("action")}>
                                <CheckSquare className="mr-2 h-3.5 w-3.5" /> Action
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}
            </div>


            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 relative">
                    {loading && <div className="absolute inset-0 z-10 bg-white/50 grid place-items-center rounded-2xl"><Loader2 className="animate-spin text-[#9A968F]" /></div>}
                    <CalendarGrid year={year} month={month} eventsByDay={eventsByDay}
                        selectedDate={selectedDate} onSelectDay={(d) => { setSelectedDate(d); setQuickItem(null); }}
                        onPrev={() => changeMonth(-1)} onNext={() => changeMonth(1)} onToday={goToday}
                        canQuickCreate={canAdd} onQuickCreate={openQuickCreate} />
                </div>

                {/* Right column: day panel OR item quick view */}
                {quickItem ? (
                    quickItem.source_module === "attendance" || quickItem.source_module === "equipment" ? (
                        <DaySummaryQuickView item={quickItem} onBack={() => setQuickItem(null)} />
                    ) : (
                    <CalendarItemQuickView
                        item={quickItem}
                        navigate={navigate}
                        canAct={canEdit && CAN_EDIT_ROLES.has(role)}
                        onChanged={load}
                        onBack={() => setQuickItem(null)}
                        onEditNative={quickItem.editable ? () => setDialog({ mode: "edit", initial: quickItem }) : null}
                        onDeleteNative={quickItem.editable ? () => deleteEvent(quickItem) : null}
                    />
                    )
                ) : (
                    <div className="rounded-2xl bg-white border border-[#E5E0DA] p-5">
                        <h4 className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">
                            {selectedDate ? new Date(selectedDate).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" }) : "Select a day"}
                        </h4>
                        <div className="mt-3 space-y-2" data-testid="calendar-day-panel">
                            {dayEvents.length === 0 && (
                                <div className="text-[12px] italic text-[#9A968F] flex items-center gap-2 py-4">
                                    <CalendarOff size={14} /> No events on this day.
                                </div>
                            )}
                            {dayEvents.map((e) => (
                                <button key={e.id} data-testid={`calendar-event-${e.source_module}-${e.source_id}`}
                                    onClick={() => setQuickItem(e)}
                                    className="w-full text-left rounded-lg border border-[#F0EDE8] p-2.5 hover:bg-[#FAF9F7] transition">
                                    <div className="flex items-start gap-2">
                                        {e.source_module === "attendance" || e.source_module === "equipment" ? (
                                            <span className="w-5 h-5 rounded-full grid place-items-center shrink-0" style={{ background: `${e.colour}1F`, color: e.colour }}>
                                                {e.source_module === "attendance" ? <Users size={11} /> : <Truck size={11} />}
                                            </span>
                                        ) : (
                                            <span className="w-2.5 h-2.5 rounded-full mt-1 shrink-0" style={{ background: e.colour }} />
                                        )}
                                        <div className="min-w-0 flex-1">
                                            <div className="text-[13px] font-semibold text-[#1F1F1F] truncate">{e.title}</div>
                                            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                                <span className="text-[10.5px] text-[#9A968F]">{e.event_type}</span>
                                                {e.time && <span className="text-[10.5px] text-[#9A968F]">· {e.time}</span>}
                                                {e.status && <Pill tone={STATUS_TONE[e.status] || "neutral"}>{e.status}</Pill>}
                                            </div>
                                        </div>
                                        {!(e.source_module === "attendance" || e.source_module === "equipment") && (
                                            <ExternalLink size={13} className="text-[#C0BBB3] shrink-0 mt-0.5" />
                                        )}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {showScopeNote && (
                <p className="text-[11px] text-[#9A968F]">
                    Calendar aggregates dates from across every module. Coloured chips are read-only links to their source record; only ad-hoc events you add here are editable.
                </p>
            )}

            {dialog && (
                <EventDialog mode={dialog.mode} initial={dialog.initial} clientId={clientId} projectId={projectId}
                    buildingId={buildingId} nativeTypes={meta.native_types} eventStatuses={meta.event_statuses}
                    onClose={() => setDialog(null)} onSaved={() => { setDialog(null); setQuickItem(null); load(); }} />
            )}

            {visitDialog.open && (
                <VisitDialog open={visitDialog.open} mode="create" initial={null}
                    prefill={{ client_id: clientId, project_id: projectId, scheduled_date: visitDialog.date }}
                    clients={clients} buildings={buildings} templates={templates} meta={visitMeta}
                    lockedClientId={clientId}
                    onClose={() => setVisitDialog({ open: false, date: null })}
                    onSaved={() => { setVisitDialog({ open: false, date: null }); load(); }} />
            )}

            {actionDialog.open && (
                <QuickActionDialog date={actionDialog.date} clientId={clientId} projectId={projectId}
                    onClose={() => setActionDialog({ open: false, date: null })}
                    onSaved={() => { setActionDialog({ open: false, date: null }); load(); }} />
            )}
        </div>
    );
}

// --- Quick action create (title/priority/assignee/due date), from the calendar's Add menu or a blank day ---
function QuickActionDialog({ date, clientId, projectId, onClose, onSaved }) {
    const [form, setForm] = useState({ title: "", priority: "Medium", assignee_name: "", due_date: date || todayIso(), description: "" });
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    async function save() {
        if (!form.title.trim()) { toast.error("Title is required"); return; }
        setBusy(true);
        try {
            await api.post("/actions", {
                client_id: clientId, project_id: projectId || undefined, title: form.title,
                priority: form.priority, assignee_name: form.assignee_name || undefined,
                due_date: form.due_date || undefined, description: form.description || undefined, source: "Manual",
            });
            toast.success("Action created");
            onSaved();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose} data-testid="calendar-action-dialog">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                <div className="px-5 py-4 border-b border-[#E5E0DA] flex items-center justify-between">
                    <h3 className="text-base font-bold text-[#1F1F1F]">New action</h3>
                    <button onClick={onClose} data-testid="calendar-action-dialog-close" className="text-[#9A968F] hover:text-[#1F1F1F]"><X size={18} /></button>
                </div>
                <div className="p-5 space-y-3">
                    <Field label="Title" required>
                        <input data-testid="calendar-action-title" value={form.title} onChange={(e) => set("title", e.target.value)}
                            className="w-full h-9 px-3 rounded-lg border border-[#E5E0DA] text-sm" placeholder="e.g. Repair fire door closer" />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Priority">
                            <select data-testid="calendar-action-priority" value={form.priority} onChange={(e) => set("priority", e.target.value)}
                                className="w-full h-9 px-2 rounded-lg border border-[#E5E0DA] text-sm">
                                {QUICK_CREATE_PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </Field>
                        <Field label="Due date">
                            <input data-testid="calendar-action-due-date" type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)}
                                className="w-full h-9 px-2 rounded-lg border border-[#E5E0DA] text-sm" />
                        </Field>
                    </div>
                    <Field label="Assignee">
                        <input data-testid="calendar-action-assignee" value={form.assignee_name} onChange={(e) => set("assignee_name", e.target.value)}
                            className="w-full h-9 px-3 rounded-lg border border-[#E5E0DA] text-sm" placeholder="Name" />
                    </Field>
                    <Field label="Notes">
                        <textarea data-testid="calendar-action-notes" value={form.description} onChange={(e) => set("description", e.target.value)}
                            rows={2} className="w-full px-3 py-2 rounded-lg border border-[#E5E0DA] text-sm resize-none" />
                    </Field>
                </div>
                <div className="px-5 py-3 border-t border-[#E5E0DA] flex justify-end gap-2">
                    <button onClick={onClose} className="px-3 py-2 text-sm font-semibold rounded-lg border border-[#E5E0DA] text-[#6F6A64]">Cancel</button>
                    <button data-testid="calendar-action-save" onClick={save} disabled={busy}
                        className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg text-white disabled:opacity-50"
                        style={{ background: "var(--brand-accent, #C75A1B)" }}>
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Create
                    </button>
                </div>
            </div>
        </div>
    );
}

// --- Native ad-hoc event create/edit dialog ---
function EventDialog({ mode, initial, clientId, projectId, buildingId, nativeTypes, eventStatuses, onClose, onSaved }) {
    const isEdit = mode === "edit";
    const [form, setForm] = useState({
        title: initial?.title || "",
        event_type: initial?.event_type || "Meeting",
        start: (initial?.start || initial?.date || todayIso()).slice(0, 10),
        end: "",
        all_day: true,
        description: "",
        status: "Scheduled",
        portal_visible: false,
    });
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    async function save() {
        if (!form.title.trim()) { toast.error("Title is required"); return; }
        setBusy(true);
        try {
            if (isEdit) {
                await api.patch(`/calendar/events/${initial.source_id}`, {
                    title: form.title, event_type: form.event_type, start: form.start,
                    description: form.description, status: form.status, portal_visible: form.portal_visible,
                });
            } else {
                await api.post("/calendar/events", {
                    client_id: clientId, project_id: projectId, building_id: buildingId,
                    title: form.title, event_type: form.event_type, start: form.start,
                    description: form.description, status: form.status, portal_visible: form.portal_visible,
                    all_day: true,
                });
            }
            toast.success(isEdit ? "Event updated" : "Event added");
            onSaved();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose} data-testid="calendar-event-dialog">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                <div className="px-5 py-4 border-b border-[#E5E0DA] flex items-center justify-between">
                    <h3 className="text-base font-bold text-[#1F1F1F]">{isEdit ? "Edit event" : "Add calendar event"}</h3>
                    <button onClick={onClose} data-testid="calendar-event-dialog-close" className="text-[#9A968F] hover:text-[#1F1F1F]"><X size={18} /></button>
                </div>
                <div className="p-5 space-y-3">
                    <Field label="Title" required>
                        <input data-testid="calendar-event-title" value={form.title} onChange={(e) => set("title", e.target.value)}
                            className="w-full h-9 px-3 rounded-lg border border-[#E5E0DA] text-sm" placeholder="e.g. Quarterly client review" />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Type">
                            <select data-testid="calendar-event-type" value={form.event_type} onChange={(e) => set("event_type", e.target.value)}
                                className="w-full h-9 px-2 rounded-lg border border-[#E5E0DA] text-sm">
                                {(nativeTypes || ["General", "Meeting", "Deadline", "Reminder", "Other"]).map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </Field>
                        <Field label="Date" required>
                            <input data-testid="calendar-event-date" type="date" value={form.start} onChange={(e) => set("start", e.target.value)}
                                className="w-full h-9 px-2 rounded-lg border border-[#E5E0DA] text-sm" />
                        </Field>
                    </div>
                    {isEdit && (
                        <Field label="Status">
                            <select data-testid="calendar-event-status" value={form.status} onChange={(e) => set("status", e.target.value)}
                                className="w-full h-9 px-2 rounded-lg border border-[#E5E0DA] text-sm">
                                {(eventStatuses || ["Scheduled", "Done", "Cancelled"]).map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </Field>
                    )}
                    <Field label="Notes">
                        <textarea data-testid="calendar-event-notes" value={form.description} onChange={(e) => set("description", e.target.value)}
                            rows={2} className="w-full px-3 py-2 rounded-lg border border-[#E5E0DA] text-sm resize-none" />
                    </Field>
                    <label className="flex items-center gap-2 text-[13px] text-[#6F6A64] cursor-pointer">
                        <input type="checkbox" data-testid="calendar-event-portal" checked={form.portal_visible} onChange={(e) => set("portal_visible", e.target.checked)} />
                        Visible to client portal
                    </label>
                </div>
                <div className="px-5 py-3 border-t border-[#E5E0DA] flex justify-end gap-2">
                    <button onClick={onClose} className="px-3 py-2 text-sm font-semibold rounded-lg border border-[#E5E0DA] text-[#6F6A64]">Cancel</button>
                    <button data-testid="calendar-event-save" onClick={save} disabled={busy}
                        className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg text-white disabled:opacity-50"
                        style={{ background: "var(--brand-accent, #C75A1B)" }}>
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {isEdit ? "Save" : "Add"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-1">
                {label}{required && <span className="text-[#C0392B] ml-0.5">*</span>}
            </label>
            {children}
        </div>
    );
}
